import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '../../shared/events.ts';
import { MEMORY_FIELD_KEYS, MEMORY_FIELDS, type UserMemory } from '../../shared/memory.ts';
import { api } from '../lib/api.ts';
import type { Snapshot } from '../session/conversation.ts';
import { useConversation } from '../session/useConversation.ts';
import { useMemory } from '../shell/useMemory.tsx';
import { useMicPermission } from '../voice/useMicPermission.ts';
import { type CallSummary, type ConversationActions, ConversationScreen } from './ConversationScreen.tsx';
import { untilCompleted } from './completion.ts';

// While the server finishes a call after answering (a slow updater): how often memory is read, and for how long. The
// server answers pending 40 s in (COMPLETE_WAIT_MS, env.ts), and the updater's Gemini call gives up at 120 s (CALL_MS,
// gemini.ts), so 90 s on the job has ended one way or the other; past that, holding Start back helps nothing. Change
// one of the three, check the others.
const COMPLETION_READ_MS = 4000;
const COMPLETION_GIVE_UP_MS = 90_000;

/** Fields that changed during the call, for the post-call "what I learned" list. */
function learned(before: UserMemory, after: UserMemory): CallSummary['items'] {
  return MEMORY_FIELD_KEYS.filter(
    (k) => after.fields[k]?.value && after.fields[k]?.value !== before.fields[k]?.value,
  ).map((k) => ({ label: MEMORY_FIELDS[k].label, value: after.fields[k]!.value }));
}

/** The Chat tab: runs the live conversation engine and post-call completion, and renders ConversationScreen. */
/** Answers whose analysis finished (the card has its result and scores). */
const settledCount = (items: Snapshot['items']) =>
  items.filter((i) => i.task?.phase === 'done' && !i.task.scoresPending).length;

export function ConversationView({
  user,
  checkInOn = false,
  onLiveChange,
  onOpenProfile,
}: {
  user: User;
  checkInOn?: boolean;
  onLiveChange?: (live: boolean) => void;
  onOpenProfile?: () => void;
}) {
  const { convo, snap } = useConversation();
  const micPermission = useMicPermission();
  const { memory, loaded, problem, retrying, retry, set: setMemory, refresh } = useMemory();
  const before = useRef<UserMemory | null>(null);
  // An answer that finishes analysing after the call's completion was sent (End tapped during the analysis) can still
  // complete the set server-side, so memory is read again then: the home screen's warm-up and start label go by
  // memory's stage. Null until the completion has settled.
  const settled = settledCount(snap.items);
  const settledAtCompletion = useRef<number | null>(null);
  const [summary, setSummary] = useState<CallSummary | null>(null);
  // Set on mount too: React's StrictMode mounts, unmounts and mounts again in development.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const live = snap.phase === 'starting' || snap.phase === 'live' || snap.phase === 'reconnecting';

  useEffect(() => onLiveChange?.(live), [live, onLiveChange]);

  // The plan arrives with the session, after the start's catch-up may have completed an earlier call this tab never
  // saw finish. Read memory again then, so the call's warm-up steps show what the server knows, as its prompt does.
  useEffect(() => {
    if (snap.plan) void refresh();
  }, [snap.plan, refresh]);

  // After a call: wait for the final event flush, then run the post-call memory update and show what changed.
  useEffect(() => {
    if (snap.phase === 'starting') {
      before.current = memory;
      settledAtCompletion.current = null;
      setSummary(null);
    }
    if ((snap.phase !== 'ended' && snap.phase !== 'error') || !snap.sessionId) return;
    if (summary?.sessionId === snap.sessionId) return;
    const id = snap.sessionId;
    const baseline = before.current;
    setSummary({ sessionId: id, status: 'saving', items: [] });
    const atRequest = settledCount(convo.getSnapshot().items);
    let failed = false;
    void convo
      .whenFlushed()
      .then(() => api.completeSession(id))
      .then(async (r) => {
        // Answered before a slow updater: the summary stays "Noting…" (Start with it, so the next call isn't planned
        // from the stage before this one) until the server's job has completed the call.
        const memory =
          r.updater === 'pending'
            ? ((await untilCompleted(() => api.memory().then((m) => m.memory), id, {
                everyMs: COMPLETION_READ_MS,
                giveUpMs: COMPLETION_GIVE_UP_MS,
                // Nobody waiting: the view went away, or a new call started (it has its own summary).
                stopped: () => !alive.current || convo.getSnapshot().sessionId !== id,
              })) ?? r.memory)
            : r.memory;
        setMemory(memory);
        setSummary((cur) =>
          cur?.sessionId === id ? { sessionId: id, status: 'done', items: learned(baseline ?? memory, memory) } : cur,
        );
      })
      .catch(() => {
        failed = true;
        setSummary((cur) => (cur?.sessionId === id ? { sessionId: id, status: 'done', items: [] } : cur));
      })
      .finally(() => {
        // An answer that landed while the completion ran may be missing from its response, and a completion that
        // failed on the way back may still have committed: read memory again, after the response was applied.
        const now = settledCount(convo.getSnapshot().items);
        settledAtCompletion.current = now;
        if (now > atRequest || failed) void refresh();
      });
  }, [snap.phase, snap.sessionId, summary, convo, memory, setMemory, refresh]);

  useEffect(() => {
    const atCompletion = settledAtCompletion.current;
    if ((snap.phase !== 'ended' && snap.phase !== 'error') || atCompletion === null || settled <= atCompletion) return;
    settledAtCompletion.current = settled;
    void refresh();
  }, [snap.phase, settled, refresh]);

  useEffect(() => () => void convo.end('unmount'), [convo]);

  const setupFinished = useCallback(() => {
    convo.dismiss();
    setSummary(null);
  }, [convo]);
  const actions = useMemo<ConversationActions>(
    () => ({
      start: () => void convo.start(),
      end: () => void convo.end(),
      sendText: (t) => convo.sendText(t),
      pick: (id, o) => convo.pick(id, o),
      finishTask: () => void convo.finishTask(),
      skipTask: () => convo.skipTask(),
      setupFinished,
    }),
    [convo, setupFinished],
  );

  return (
    <ConversationScreen
      user={user}
      snap={snap}
      memory={memory}
      loaded={loaded}
      checkInOn={checkInOn}
      micPermission={micPermission}
      loadProblem={problem}
      retryingLoad={retrying}
      // Signed out: only a fresh start (sign-in, then memory) helps.
      onRetryLoad={problem === 'signed-out' ? () => window.location.reload() : () => void retry()}
      summary={summary}
      actions={actions}
      onOpenProfile={onOpenProfile}
    />
  );
}
